import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export function Invite() {
  const { token } = useParams()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [previewError, setPreviewError] = useState(null)

  const [accepting, setAccepting] = useState(false)
  const [acceptResult, setAcceptResult] = useState(null) // 'success' | 'already_member'
  const [acceptError, setAcceptError] = useState(null)

  useEffect(() => {
    if (!token) {
      setPreviewError('Invalid invite link.')
      setPreviewLoading(false)
      return
    }

    const fetchPreview = async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/get-invite-preview?token=${encodeURIComponent(token)}`,
          {
            headers: {
              apikey: SUPABASE_ANON_KEY,
            },
          }
        )

        const json = await res.json()

        if (!res.ok) {
          setPreviewError(json.error || 'This invite link is invalid or has expired.')
        } else {
          setPreview(json)
        }
      } catch (err) {
        console.error('Preview fetch error:', err)
        setPreviewError('Could not load invite. Please check your connection and try again.')
      } finally {
        setPreviewLoading(false)
      }
    }

    fetchPreview()
  }, [token])

  const handleAccept = async () => {
    if (!user) return

    setAccepting(true)
    setAcceptError(null)

    try {
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: { token },
      })

      if (error) {
        // data?.error has the real message from the edge function body
        throw new Error(data?.error || error.message || 'Something went wrong. Please try again.')
      }

      if (data?.already_member) {
        setAcceptResult('already_member')
      } else {
        setAcceptResult('success')
      }
    } catch (err) {
      console.error('Accept error:', err)
      setAcceptError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  // Show loading while auth or preview is resolving
  if (authLoading || previewLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-3xl bg-amber-500 flex items-center justify-center">
            <span className="text-3xl">🔆</span>
          </div>
          <p className="text-stone-400 text-sm animate-pulse">Loading invite...</p>
        </div>
      </div>
    )
  }

  // Link is maxed out or expired (preview loaded but valid: false)
  if (preview && !preview.valid) {
    const reason = preview.expired
      ? 'This invite link has expired.'
      : 'This invite link has already been used.'
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-900 px-5">
        <div className="w-full max-w-sm text-center">
          <div className="w-20 h-20 rounded-3xl bg-stone-800 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🔗</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-100 mb-3">Link Unavailable</h1>
          <p className="text-stone-400 mb-8">{reason}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white font-semibold transition-colors"
          >
            Go to The Beacon
          </Link>
        </div>
      </div>
    )
  }

  // Invalid or expired link
  if (previewError) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-900 px-5">
        <div className="w-full max-w-sm text-center">
          <div className="w-20 h-20 rounded-3xl bg-stone-800 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🔗</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-100 mb-3">Link Unavailable</h1>
          <p className="text-stone-400 mb-8">{previewError}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white font-semibold transition-colors"
          >
            Go to The Beacon
          </Link>
        </div>
      </div>
    )
  }

  const inviterName = preview?.owner?.display_name || 'Someone'
  const circleName = preview?.circle?.name || 'Circle of Trust'
  const circleEmoji = preview?.circle?.emoji || '🔆'

  // Success: joined
  if (acceptResult === 'success') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-900 px-5">
        <div className="w-full max-w-sm text-center">
          <div className="w-24 h-24 rounded-3xl bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">✅</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-100 mb-2">You're in!</h1>
          <p className="text-stone-400 mb-8">
            You've joined {inviterName}'s <span className="text-stone-200 font-medium">{circleEmoji} {circleName}</span> on The Beacon.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white font-semibold transition-colors"
            type="button"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Already a member
  if (acceptResult === 'already_member') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-900 px-5">
        <div className="w-full max-w-sm text-center">
          <div className="w-24 h-24 rounded-3xl bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">🤝</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-100 mb-2">Already connected</h1>
          <p className="text-stone-400 mb-8">
            You're already in {inviterName}'s <span className="text-stone-200 font-medium">{circleEmoji} {circleName}</span>.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white font-semibold transition-colors"
            type="button"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Not logged in — show preview with sign up / log in buttons
  if (!user) {
    const redirectBack = `/invite/${token}`

    return (
      <div className="min-h-[100dvh] flex flex-col bg-stone-900 px-5 pt-16 pb-10">
        {/* Beacon wordmark */}
        <div className="flex items-center gap-2 mb-12">
          <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
            <span className="text-lg">🔆</span>
          </div>
          <span className="text-stone-100 font-bold text-lg tracking-tight">The Beacon</span>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center">
          {/* Circle icon */}
          <div className="w-24 h-24 rounded-3xl bg-stone-800 border-2 border-stone-700 flex items-center justify-center mb-6">
            <span className="text-5xl">{circleEmoji}</span>
          </div>

          <p className="text-stone-400 text-base mb-2">
            <span className="text-stone-100 font-semibold">{inviterName}</span> invited you to their
          </p>
          <h1 className="text-3xl font-bold text-stone-100 mb-2">
            {circleName}
          </h1>
          <p className="text-stone-500 text-sm mb-12">
            Stay loosely but meaningfully connected — no feeds, no noise.
          </p>

          <div className="w-full max-w-xs space-y-3">
            <Link
              to={`/auth?redirect=${encodeURIComponent(redirectBack)}&mode=signup`}
              className="w-full flex items-center justify-center py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white font-semibold text-base transition-colors"
            >
              Create an account
            </Link>
            <Link
              to={`/auth?redirect=${encodeURIComponent(redirectBack)}&mode=login`}
              className="w-full flex items-center justify-center py-3.5 rounded-2xl bg-stone-800 hover:bg-stone-700 active:bg-stone-900 text-stone-100 font-semibold text-base border border-stone-700 transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-stone-600 mt-8">
          The Beacon — Stay connected with people who matter.
        </p>
      </div>
    )
  }

  // Logged in — show accept button
  return (
    <div className="min-h-[100dvh] flex flex-col bg-stone-900 px-5 pt-16 pb-10">
      {/* Beacon wordmark */}
      <div className="flex items-center gap-2 mb-12">
        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
          <span className="text-lg">🔆</span>
        </div>
        <span className="text-stone-100 font-bold text-lg tracking-tight">The Beacon</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        {/* Circle icon */}
        <div className="w-24 h-24 rounded-3xl bg-stone-800 border-2 border-stone-700 flex items-center justify-center mb-6">
          <span className="text-5xl">{circleEmoji}</span>
        </div>

        <p className="text-stone-400 text-base mb-2">
          <span className="text-stone-100 font-semibold">{inviterName}</span> invited you to their
        </p>
        <h1 className="text-3xl font-bold text-stone-100 mb-2">
          {circleName}
        </h1>
        <p className="text-stone-500 text-sm mb-12">
          Joining means {inviterName} can send you check-ins and see your status.
        </p>

        {acceptError && (
          <div className="w-full max-w-xs mb-4 px-4 py-3 rounded-2xl bg-red-900/30 border border-red-800 text-red-300 text-sm">
            {acceptError}
          </div>
        )}

        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60 text-white font-semibold text-base transition-colors"
            type="button"
          >
            {accepting ? 'Joining...' : `Join ${inviterName}'s circle`}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 rounded-2xl bg-transparent text-stone-400 hover:text-stone-200 font-medium text-sm transition-colors"
            type="button"
          >
            Maybe later
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-stone-600 mt-8">
        The Beacon — Stay connected with people who matter.
      </p>
    </div>
  )
}

export default Invite
